import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../axiosInterceptor';

type ScheduleSessionPayload = {
  studentId: string;
  date: string;
  time: string;
  meetingPlatform?: string;
  title?: string;
  description?: string;
  duration: number;
  status?: string;
  notes?: string;
  timezone?: string;
};

type ScheduleSessionState = {
  loading: boolean;
  error: boolean;
  errorMessage: string;
  data: unknown | null;
};

const initialState: ScheduleSessionState = {
  loading: false,
  error: false,
  errorMessage: '',
  data: null,
};

export const scheduleSession = createAsyncThunk<
  unknown,
  ScheduleSessionPayload
>('scheduleSession', async (payload) => {
  const {
    studentId,
    date,
    time,
    meetingPlatform,
    title,
    description,
    duration,
    status,
    notes,
    timezone,
  } = payload;

  if (!studentId) {
    throw new Error('Missing student ID.');
  }
  if (!date || !time) {
    throw new Error('Missing date or time.');
  }
  const resolvedPlatform =
    meetingPlatform === 'google-meet' ? 'google' : meetingPlatform;
  if (!duration && resolvedPlatform !== 'google') {
    throw new Error('Missing duration.');
  }

  if (resolvedPlatform === 'google') {
    const sanitizedTime =
      time.split(':').length > 2 ? time.split(':').slice(0, 2).join(':') : time;
    const requestPayload = {
      title: 'Strategy Session',
      date,
      time: sanitizedTime,
      meeting_platform: 'google',
    };
    console.log('📤 [BOOKING SESSION - GOOGLE MEET] Sending to backend:', {
      endpoint: `/api/bookings/students/${studentId}/meet`,
      payload: requestPayload,
      originalTime: time,
      sanitizedTime,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
    const { data } = await api.post(
      `/api/bookings/students/${studentId}/meet`,
      requestPayload
    );
    console.log('✅ [BOOKING SESSION - GOOGLE MEET] Response from backend:', data);
    return data;
  }

  const requestPayload = {
    date,
    time,
    meeting_platform: resolvedPlatform ?? 'in-house',
    title: title ?? 'Coaching Session',
    description: description ?? '',
    duration,
    status: status ?? 'scheduled',
    notes: notes ?? '',
    timezone: timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
  
  console.log('📤 [BOOKING SESSION] Sending to backend:', {
    endpoint: `/api/bookings/students/${studentId}/sessions`,
    payload: requestPayload,
    userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  const { data } = await api.post(
    `/api/bookings/students/${studentId}/sessions`,
    requestPayload
  );

  console.log('✅ [BOOKING SESSION] Response from backend:', data);
  return data;
});

const scheduleSessionSlice = createSlice({
  name: 'scheduleSession',
  initialState,
  reducers: {
    resetScheduleSession: (state) => {
      state.loading = false;
      state.error = false;
      state.errorMessage = '';
      state.data = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(scheduleSession.pending, (state) => {
      state.loading = true;
      state.error = false;
      state.errorMessage = '';
    });
    builder.addCase(scheduleSession.fulfilled, (state, action) => {
      state.loading = false;
      state.data = action.payload;
    });
    builder.addCase(scheduleSession.rejected, (state, action) => {
      state.loading = false;
      state.error = true;
      state.errorMessage = action.error.message ?? 'Failed to schedule session.';
    });
  },
});

export const { resetScheduleSession } = scheduleSessionSlice.actions;
export default scheduleSessionSlice.reducer;
